import { api } from 'api';
import { ClaimResolutionType, ServerActionName } from 'api/endpoints';
import { useApiQuery } from 'hooks/useApiQuery';
import { useCallback } from 'react';

const resolutionTypesEmpty: ClaimResolutionType[] = [];

const actionNone: ServerActionName = {
  id: 0,
  actionName: 'None',
  resolutionTypeId: 1,
  resolutionTypeName: '',
  actionSubStatusId: 0,
  actionSubStatusName: '',
};

const actionNameItemsEmpty = [actionNone];

export const useClaimResolutionTypeProvider = () => {
  const resolutionTypes = useApiQuery({
    queryFn: api.claims.resolutionTypes,
  });

  const actionNames = useApiQuery({
    queryFn: api.actionNames.getNames,
  });

  const actionNameItems = actionNames.data ?? actionNameItemsEmpty;

  //------ Resolution Items ----------------------------------------------------
  const resolutionStatusList = resolutionTypes.data ?? resolutionTypesEmpty;

  const resolutionStatus = useCallback(
    (statusId: number | undefined) =>
      resolutionStatusList.find((r) => r.resolutionTypeId === statusId),
    [resolutionStatusList],
  );

  const resolutionSubstatusList = useCallback(
    (statusId: number | undefined = 1) => {
      const status = resolutionStatus(statusId);
      return status?.resolutionSubTypes || [];
    },
    [resolutionStatus],
  );

  const resolutionSubstatusDefault = resolutionStatusList[0]?.resolutionSubTypes[0];
  const resolutionSubstatus = useCallback(
    (statusId: number | undefined, subStatusId: number | undefined = 0) => {
      const subStatuses = resolutionSubstatusList(statusId);
      const subStatus = subStatuses.find((r) => r.actionSubStatusId === subStatusId);
      return subStatus ?? resolutionSubstatusDefault;
    },
    [resolutionSubstatusDefault, resolutionSubstatusList],
  );

  const workflowStatusListDefault = resolutionSubstatusDefault?.workflowStatuses;
  const workflowStatusList = useCallback(
    (statusId: number | undefined, subStatusId: number | undefined) => {
      const subStatus = resolutionSubstatus(statusId, subStatusId);
      return subStatus?.workflowStatuses ?? [];
    },
    [resolutionSubstatus],
  );

  const resolutionNameList = useCallback(
    (
      statusId: number | undefined = 0,
      subStatusId: number | undefined = 0,
      options: { skipNone?: boolean } = {},
    ) => {
      const items =
        actionNameItems.filter(
          (r) =>
            r.resolutionTypeId === statusId && r.actionSubStatusId === subStatusId,
        ) ?? [];

      const hasNoneAction = items.some((r) => r.id === actionNone.id);
      const itemsWithNone = hasNoneAction ? items : [actionNone, ...items];
      const resultItems = options.skipNone
        ? itemsWithNone.filter((r) => r.id !== actionNone.id)
        : itemsWithNone;

      return resultItems;
    },
    [actionNameItems],
  );

  //------ additional functions ----------------------------------------------

  const isHearingDateRequired = useCallback(
    (statusId: number | undefined, subStatusId: number | undefined) => {
      const subStatus = resolutionSubstatus(statusId, subStatusId);
      return subStatus?.hearingDateRequired ?? false;
    },
    [resolutionSubstatus],
  );

  //------------------------------------------------------------------------------

  return {
    actionNames: actionNameItems,
    refreshActionNames: actionNames.fetch,
    resolutionStatus,
    resolutionStatusList,
    resolutionSubstatusDefault,
    resolutionSubstatus,
    resolutionSubstatusList,
    workflowStatusListDefault,
    workflowStatusList,
    resolutionNameList,

    isHearingDateRequired,
  };
};
